import { useEffect, useMemo, useState } from "react";
import ArtworkCard from "@/components/ArtworkCard";
import SEOHead from "@/components/SEOHead";
import { getArtworks, http } from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function Gallery() {
  const [all, setAll] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getArtworks(),
      http.get("/artworks/categories").then((r) => r.data.categories || []),
    ])
      .then(([works, cats]) => {
        if (cancelled) return;
        setAll(works);
        setCategories(cats);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const filters = useMemo(
    () => ["All", ...categories],
    [categories]
  );

  const items = useMemo(
    () => (filter === "All" ? all : all.filter((a) => a.category === filter)),
    [all, filter]
  );

  return (
    <>
      <SEOHead
        title="Artwork by Colin Darke | Paintings, Drawings & Ambidextrous Art"
        description="Browse the archive of Colin Darke's artwork — paintings, drawings, portraits and ambidextrous work. Preview each piece in your own room."
        path="/artwork"
      />

      <section className="pt-16 pb-6 border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">Archive</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            The artwork.
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-[hsl(var(--ink-muted))]">
            Selected works spanning ambidextrous compositions, paintings,
            portraiture and early studies. Tap any piece to preview it in
            your own room.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-20 z-30 bg-[hsl(var(--bg))]/95 backdrop-blur border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 py-4 flex gap-2 overflow-x-auto scrollbar-none">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`gallery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-colors ${
                filter === f
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))] border-[hsl(var(--ink))]"
                  : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          {loading ? (
            <p className="catalog-label text-[hsl(var(--ink-muted))]" data-testid="gallery-loading">
              Loading archive…
            </p>
          ) : items.length === 0 ? (
            <div
              className="py-16 text-center border border-dashed border-[hsl(var(--line))]"
              data-testid="gallery-empty"
            >
              <p className="catalog-label mb-2">No works in {filter}</p>
              <p className="font-serif text-2xl">
                This category isn't populated yet. Try another filter.
              </p>
              <button
                onClick={() => setFilter("All")}
                className="mt-6 inline-flex items-center gap-2 border border-[hsl(var(--ink))] px-5 py-2 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors"
              >
                See all works
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {items.map((a, i) => (
                <ArtworkCard
                  key={a.id}
                  artwork={a}
                  priority={i < 3 ? "high" : "medium"}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
